// frontend/src/components/CampaignManager/MobileCards.tsx
import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Stack,
  Divider
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import {
  Lottery,
  Callcenter,
  Campaign,
  Werbeweg,
  Werbetraeger,
  Vermittler,
  Action,
  Versand,
  PhoneNumber
} from './types';
import { formatDate } from '../../utils/dateUtils';

// Format WKZ with leading zeros to 5 digits
const formatWKZ = (wkz: string | number | undefined | null): string => {
  if (wkz === undefined || wkz === null || wkz === '') return '-';
  const str = String(wkz).replace(/^0+/, ''); // Remove existing leading zeros first
  return str.padStart(5, '0');
};

// Strip lottery suffix from campaign name (e.g. "Inaktive - SKL 147" -> "Inaktive")
const stripLotterySuffix = (name: string): string => {
  if (!name) return name;
  return name.replace(/\s+-\s+(SKL|NKL|GKL)\s+\d+$/i, '').trim();
};

const MobileCard = ({ children, ...props }: any) => (
  <Card variant="outlined" sx={{ mb: 2, ...props.sx }}>
    <CardContent>{children}</CardContent>
  </Card>
);

interface LotteryCardProps {
  lottery: Lottery;
  onEdit: (lottery: Lottery) => void;
  onDelete: (id: number) => void;
}

export const LotteryCard: React.FC<LotteryCardProps> = ({ lottery, onEdit, onDelete }) => (
  <MobileCard>
    <Stack spacing={1}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">{lottery.name}</Typography>
        <Box>
          <IconButton size="small" onClick={() => onEdit(lottery)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => onDelete(lottery.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary">
        Kurzname: {lottery.name_kurz}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Chip label={`Jahr: ${lottery.year}`} size="small" />
        <Chip
          label={lottery.type}
          color={lottery.type === 'SKL' ? 'primary' : 'secondary'}
          size="small"
        />
        <Chip
          label={formatDate(lottery.startdatum)}
          size="small"
          variant="outlined"
        />
      </Box>
    </Stack>
  </MobileCard>
);

interface CallcenterCardProps {
  callcenter: Callcenter;
  phoneNumbers: PhoneNumber[];
  onEdit: (callcenter: Callcenter) => void;
  onDelete: (id: number) => void;
  onAddPhone: (callcenterId: number) => void;
  onEditPhone: (phone: PhoneNumber) => void;
  onDeletePhone: (id: number) => void;
}

export const CallcenterCard: React.FC<CallcenterCardProps> = ({ 
  callcenter, 
  phoneNumbers,
  onEdit, 
  onDelete,
  onAddPhone,
  onEditPhone,
  onDeletePhone
}) => {
  const ccPhones = phoneNumbers.filter(p => p.callcenter_id === callcenter.id);
  
  return (
    <MobileCard>
      <Stack spacing={1.5}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{callcenter.name}</Typography>
          <Box>
            <IconButton size="small" onClick={() => onEdit(callcenter)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" color="error" onClick={() => onDelete(callcenter.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Chips */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip label={callcenter.name_kurz} size="small" color="primary" />
          <Chip
            label={callcenter.active ? 'Aktiv' : 'Inaktiv'}
            color={callcenter.active ? 'success' : 'default'}
            size="small"
          />
          <Chip
            icon={<PhoneIcon />}
            label={`${ccPhones.length} Nummer${ccPhones.length !== 1 ? 'n' : ''}`}
            size="small"
            variant="outlined"
          />
        </Box>

        {/* Phone Numbers Section */}
        {ccPhones.length > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Telefonnummern
                </Typography>
                <IconButton 
                  size="small" 
                  color="primary"
                  onClick={() => onAddPhone(callcenter.id)}
                >
                  <PhoneIcon fontSize="small" />
                </IconButton>
              </Box>
              <Stack spacing={1}>
                {ccPhones.map((phone) => (
                  <Box
                    key={phone.id_tel_nr}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 1,
                      bgcolor: 'grey.50',
                      borderRadius: 1
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {phone.nummer}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                          label={phone.type}
                          color={phone.type === 'inbound' ? 'info' : 'warning'}
                          size="small"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                        <Chip
                          label={phone.active ? 'Aktiv' : 'Inaktiv'}
                          color={phone.active ? 'success' : 'default'}
                          size="small"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                      {phone.bemerkungen && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          {phone.bemerkungen}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', ml: 1 }}>
                      <IconButton size="small" onClick={() => onEditPhone(phone)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => onDeletePhone(phone.id_tel_nr)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Box>
          </>
        )}

        {/* Add Phone Button if no phones */}
        {ccPhones.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 1 }}>
            <IconButton 
              size="small" 
              color="primary"
              onClick={() => onAddPhone(callcenter.id)}
            >
              <PhoneIcon />
            </IconButton>
            <Typography variant="caption" color="text.secondary" display="block">
              Telefonnummer hinzufügen
            </Typography>
          </Box>
        )}
      </Stack>
    </MobileCard>
  );
};

interface CampaignCardProps {
  campaign: Campaign;
  onEdit: (campaign: Campaign) => void;
  onDelete: (id: number) => void;
}

export const CampaignCard: React.FC<CampaignCardProps> = ({ campaign, onEdit, onDelete }) => (
  <MobileCard>
    <Stack spacing={1}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">{stripLotterySuffix(campaign.name)}</Typography>
        <Box>
          <IconButton size="small" onClick={() => onEdit(campaign)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => onDelete(campaign.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      <Typography variant="body2">
        {formatDate(campaign.start_date)}
      </Typography>
      <Chip label={campaign.status} size="small" sx={{ width: 'fit-content' }} />
    </Stack>
  </MobileCard>
);

interface WerbewegCardProps {
  werbeweg: Werbeweg;
  onEdit: (werbeweg: Werbeweg) => void;
  onDelete: (id: number) => void;
}

export const WerbewegCard: React.FC<WerbewegCardProps> = ({ werbeweg, onEdit, onDelete }) => (
  <MobileCard>
    <Stack spacing={1}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">{werbeweg.bezeichnung}</Typography>
        <Box>
          <IconButton size="small" onClick={() => onEdit(werbeweg)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => onDelete(werbeweg.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Chip label={werbeweg.kurz} color="primary" size="small" />
        <Chip
          label={werbeweg.active ? 'Aktiv' : 'Inaktiv'}
          color={werbeweg.active ? 'success' : 'default'}
          size="small"
        />
      </Box>
      {werbeweg.bemerkungen && (
        <Typography variant="body2" color="text.secondary">
          {werbeweg.bemerkungen}
        </Typography>
      )}
    </Stack>
  </MobileCard>
);

interface WerbetraegerCardProps {
  werbetraeger: Werbetraeger;
  onEdit: (werbetraeger: Werbetraeger) => void;
  onDelete: (id: number) => void;
}

export const WerbetraegerCard: React.FC<WerbetraegerCardProps> = ({ werbetraeger, onEdit, onDelete }) => (
  <MobileCard>
    <Stack spacing={1}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">{werbetraeger.bezeichnung}</Typography>
        <Box>
          <IconButton size="small" onClick={() => onEdit(werbetraeger)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => onDelete(werbetraeger.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Chip label={werbetraeger.kurz} color="secondary" size="small" />
        <Chip
          label={werbetraeger.active ? 'Aktiv' : 'Inaktiv'}
          color={werbetraeger.active ? 'success' : 'default'}
          size="small"
        />
      </Box>
      {werbetraeger.bemerkungen && (
        <Typography variant="body2" color="text.secondary">
          {werbetraeger.bemerkungen}
        </Typography>
      )}
    </Stack>
  </MobileCard>
);

interface VermittlerCardProps {
  vermittler: Vermittler;
  onEdit: (vermittler: Vermittler) => void;
  onDelete: (id: number) => void;
}

export const VermittlerCard: React.FC<VermittlerCardProps> = ({ vermittler, onEdit, onDelete }) => (
  <MobileCard>
    <Stack spacing={1}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">{vermittler.bezeichnung}</Typography>
        <Box>
          <IconButton size="small" onClick={() => onEdit(vermittler)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => onDelete(vermittler.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Chip label={vermittler.kurz} color="info" size="small" />
        <Chip
          label={vermittler.active ? 'Aktiv' : 'Inaktiv'}
          color={vermittler.active ? 'success' : 'default'}
          size="small"
        />
      </Box>
      {vermittler.bemerkungen && (
        <Typography variant="body2" color="text.secondary">
          {vermittler.bemerkungen}
        </Typography>
      )}
    </Stack>
  </MobileCard>
);

interface ActionCardProps {
  action: Action;
  onEdit: (action: Action) => void;
  onDelete: (id: number) => void;
}

export const ActionCard: React.FC<ActionCardProps> = ({ action, onEdit, onDelete }) => (
  <MobileCard>
    <Stack spacing={1}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">{stripLotterySuffix(action.campaign_name || '') || `Kampagne ${action.campaign_id}`}</Typography>
        <Box>
          <IconButton size="small" onClick={() => onEdit(action)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => onDelete(action.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      {action.key_aktion && (
        <Typography variant="body2" color="text.secondary">
          <strong>WKZ:</strong> <span style={{ fontFamily: 'monospace' }}>{formatWKZ(action.key_aktion)}</span>
        </Typography>
      )}
      {action.wkz && (
        <Typography variant="body2" color="text.secondary">
          <strong>WKZ-Text:</strong> {action.wkz}
        </Typography>
      )}
      {action.aktiv !== undefined && (
        <Chip
          label={action.aktiv !== false ? "Aktiv" : "Inaktiv"}
          size="small"
          color={action.aktiv !== false ? "success" : "default"}
        />
      )}
    </Stack>
  </MobileCard>
);

interface VersandCardProps {
  versand: Versand;
  onEdit: (versand: Versand) => void;
  onDelete: (id: number) => void;
}

export const VersandCard: React.FC<VersandCardProps> = ({ versand, onEdit, onDelete }) => (
  <MobileCard>
    <Stack spacing={1}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          {stripLotterySuffix(versand.kampagne_name || '') || `Kampagne ${versand.kampagnen_id}`}
        </Typography>
        <Box>
          <IconButton size="small" onClick={() => onEdit(versand)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => onDelete(versand.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary">
        {versand.werbeweg_name || `Werbeweg ${versand.werbeweg_id}`}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {versand.porto && (
          <Chip label={`Porto: ${versand.porto} €`} size="small" variant="outlined" />
        )}
        {versand.gutschrift_betrag && (
          <Chip label={`Gutschrift: ${versand.gutschrift_betrag} €`} size="small" color="success" />
        )}
        {versand.gutschrift_prozent && (
          <Chip label={`Gutschrift: ${versand.gutschrift_prozent}%`} size="small" color="success" />
        )}
      </Box>
    </Stack>
  </MobileCard>
);
